import { createSlice } from "@reduxjs/toolkit";

interface IState {
  filter: any;
  statisticRankings: any;
  rankEmployees: Array<any>;
}

const initialState: IState = {
  statisticRankings: {
    from: null,
    to: null,
    data: [],
    pagination: {
      currentPage: 1,
      total: 0,
      lastPage: null,
      itemPerPage: 10,
      isStopLoading: false,
      limit: 20,
    },
    isScrollLoading: false,
    isLoadingApi: false,

    totalProcessOfEmployees: [],
    avgProductProcessData: [],
    avgTotalFastestTime: 0,
    working_hours_data: [],
  },

  filter: {
    type: "month", // month, year, half_year,
    isAllTimeType: false,
    month: null,
    year: null,
    day: null,
    from: null,
    to: null,
    branches: [],
    departments: [],
    users: [],
    roles: [],
    allTimeFlg: false,
    total_working_hours: false,

    currentClass: null,
    currentCategory: null,
    products: [],

    modals: {
      showRole: false,
      showBranch: false,
      showDepart: false,
      showProduct: false,
    },
  },

  rankEmployees: [],
};

const mapTotalProcessOfEmployee = (data: Array<any>) => {
  return data.map((employee: any) => ({
    employee_id: employee.id,
    name_mei: employee.name_mei,
    name_sei: employee.name_sei,
    evm_user_role: employee.evm_user_role,
    user_type: employee.user_type,
    total_fastest_time: employee.total_fastest_time,
    total_ranking: employee.total_ranking,
  }));
};

const mapRankingData = (
  data: Array<any>,
  productProcesses: Array<any>,
  avgProductProcessData: Array<any>
) => {
  let response: any = [];
  productProcesses.forEach((process: any, processIndex: number) => {
    let rankEmployees = data
      .map((employee: any) => {
        return employee.product_process_ranking_data
          .filter((rank: any) => rank.process_id === process.id)
          .map((item: any, itemIndex: number) => ({
            ...item,
            employee_id: employee.id,
            name_mei: employee.name_mei,
            name_sei: employee.name_sei,
            evm_user_role: employee.evm_user_role,
            user_type: employee.user_type,
            avg_fastest_time_in_period:
              avgProductProcessData[processIndex]?.avg_fastest_time_in_period,
          }));
      })
      .flat();

    response.push({
      process_id: process.id,
      process_name: process.name,
      rankEmployees: rankEmployees,
    });
  });

  return response;
};

function roundDownToMinute(seconds) {
  return Math.floor(seconds / 60) * 60;
}

function mapWorkingHoursDataToProcess(
  data: Array<any> | null,
  productProcesses: Array<any>
) {
  const mapped = productProcesses.map((process: any) => {
    const users = data?.users?.map((user) => ({
      ...user,
      product_process_data: user.product_process_data.find(
        (each) => each.process_id === process.id
      ),
    }));
    const total = data?.quantity_product_process?.find(
      (quantity: any) => quantity.process_id === process.id
    );
    return {
      ...process,
      users,
      total,
    };
  });

  const processTotal = {
    id: -1,
    name: '合計',
    users: data?.users,
    total: {
      total_time: mapped.reduce(
        (acc: any, process: any) => acc + roundDownToMinute(process.total?.total_time || 0),
        0
      ),
      total_product: mapped.reduce(
        (acc: any, process: any) => acc + (process.total?.total_product || 0),
        0
      ),
    }
  }

  return [...mapped, processTotal ];
}

export const statisticRankingSlice = createSlice({
  name: "statisticRanking",
  initialState,
  reducers: {
    resets(state) {
      state.statisticRankings = { ...initialState.statisticRankings };
      state.filter = { ...initialState.filter };
      state.rankEmployees = [];
    },

    resetRankings(state) {
      state.statisticRankings = { ...initialState.statisticRankings };
      state.statisticRankings.isScrollLoading = false;
    },

    resetFilter(state) {
      state.filter = { ...initialState.filter };
    },

    setIsLoadingRankings(state, action) {
      state.statisticRankings.isLoadingApi = action.payload;
    },

    setRankEmployees(state, action) {
      state.rankEmployees = [...action.payload];
    },

    fetchRankings(state, action) {
      let statisticRankings = state.statisticRankings;
      const { productProcesses, data, total_working_hours } = action.payload;

      if (total_working_hours) {
        statisticRankings.data = data.users;
        statisticRankings.quantity_product_process = data.quantity_product_process
        statisticRankings.working_hours_data = mapWorkingHoursDataToProcess(
          data,
          productProcesses
        );
        state.statisticRankings = { ...statisticRankings };
      } else {
        let response = mapRankingData(
          data.users,
          productProcesses,
          data.avg_product_process_data
        );
        statisticRankings.data = [...response];
        statisticRankings.totalProcessOfEmployees = mapTotalProcessOfEmployee(
          data.users
        );
        statisticRankings.avgProductProcessData = data.avg_product_process_data;
        statisticRankings.avgTotalFastestTime = data.avg_total_fastest_time;

        state.statisticRankings = { ...statisticRankings };
      }
    },

    updateFilter(state, action) {
      let filter = { ...state.filter };
      let data = action.payload;

      for (let prop in data) {
        filter[prop] = data[prop];
      }
      state.filter = filter;
    },

    setFilter(state, action) {
      let { key, val } = action.payload;
      let filter = { ...state.filter };
      // handle modals
      if (filter.modals.hasOwnProperty(key)) {
        for (let prop in filter.modals) {
          filter.modals[prop] = false;
        }
        filter.modals[key] = val;
      } else {
        // handle prop
        filter[key] = val;
      }

      state.filter = filter;
    },

    setIsScrollLoading(state, action) {
      state.statisticRankings.isScrollLoading = action.payload;
    },

    resetModal(state) {
      let initFilter = { ...initialState.filter };
      state.filter.modals = { ...initFilter.modals };
    },
  },
});

export default statisticRankingSlice;
export const orderGroupReducer = statisticRankingSlice.reducer;
export const statisticRankingSliceAction = statisticRankingSlice.actions;
